/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Client-side date helpers.
 *
 * ── THE CLIENT HALF OF THE DATE CONTRACT ────────────────────────────────────
 * The server is authoritative. `GET /api/today` returns the household's real
 * today, computed from the SERVER's system clock in the household's timezone.
 * The browser's clock is not trusted for anything the user sees.
 *
 * This module therefore contains:
 *   • pure arithmetic on "YYYY-MM-DD" strings (the same UTC-noon technique the
 *     server uses, so a DST boundary can't shift a week)
 *   • formatting
 *   • NO way to ask "what day is it". That answer only ever comes from the
 *     server, via useToday().
 *
 * Deliberately mirrors server/dates.ts. The two are tested independently
 * against the same expectations, so a divergence shows up as a failing test
 * rather than a week grid that's off by one.
 */

export type YMD = string;
export type WeekStart = 1 | 7;

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

export function isValidYMD(v: unknown): v is YMD {
  if (typeof v !== "string" || !YMD_RE.test(v)) return false;
  const [y, m, d] = v.split("-").map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function toUtcMillis(ymd: YMD): number {
  const [y, m, d] = ymd.split("-").map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

function fromUtcMillis(ms: number): YMD {
  const dt = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

/** Add whole days. UTC-based, so a DST transition can't shift the result. */
export function addDays(ymd: YMD, n: number): YMD {
  return fromUtcMillis(toUtcMillis(ymd) + n * MS_PER_DAY);
}

export function daysBetween(a: YMD, b: YMD): number {
  return Math.round((toUtcMillis(b) - toUtcMillis(a)) / MS_PER_DAY);
}

/** ISO weekday, 1 = Monday … 7 = Sunday. */
export function isoWeekday(ymd: YMD): number {
  return ((new Date(toUtcMillis(ymd)).getUTCDay() + 6) % 7) + 1;
}

export function weekStart(ymd: YMD, weekStartsOn: WeekStart = 1): YMD {
  const wd = isoWeekday(ymd);
  return addDays(ymd, -(weekStartsOn === 1 ? wd - 1 : wd % 7));
}

export function weekEnd(ymd: YMD, weekStartsOn: WeekStart = 1): YMD {
  return addDays(weekStart(ymd, weekStartsOn), 6);
}

export function dateRange(from: YMD, to: YMD, maxDays = 62): YMD[] {
  const span = daysBetween(from, to);
  if (span < 0) return [];
  const n = Math.min(span + 1, maxDays);
  return Array.from({ length: n }, (_, i) => addDays(from, i));
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatting
//
// Every formatter passes `timeZone: "UTC"` because the value is ALREADY a bare
// calendar day. Letting Intl apply the browser's zone would re-interpret
// "2026-08-02" as an instant and render the 1st for anyone west of UTC — the
// exact bug the server-side DATE parser exists to prevent, reintroduced at the
// last step.
// ─────────────────────────────────────────────────────────────────────────────

export function formatDay(ymd: YMD, locale = "en-US"): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: "UTC", weekday: "short", day: "numeric", month: "short",
  }).format(new Date(toUtcMillis(ymd)));
}

export function formatWeekday(ymd: YMD, locale = "en-US"): string {
  return new Intl.DateTimeFormat(locale, { timeZone: "UTC", weekday: "short" })
    .format(new Date(toUtcMillis(ymd)));
}

export function formatDayNumber(ymd: YMD, locale = "en-US"): string {
  return new Intl.DateTimeFormat(locale, { timeZone: "UTC", day: "numeric" })
    .format(new Date(toUtcMillis(ymd)));
}

export function formatRange(from: YMD, to: YMD, locale = "en-US"): string {
  return `${formatDay(from, locale)} – ${formatDay(to, locale)}`;
}

/**
 * "Today" / "Tomorrow" / "Yesterday" / "Thu 6 Aug", relative to the SERVER's
 * today. `today` must come from useToday(), never from `new Date()`.
 */
export function relativeDayLabel(ymd: YMD, today: YMD, locale = "en-US"): string {
  const diff = daysBetween(today, ymd);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return formatDay(ymd, locale);
}

/**
 * Milliseconds until the next local midnight.
 *
 * Used to schedule a refresh so an app left open overnight rolls over instead
 * of highlighting yesterday. Uses the BROWSER's clock deliberately: this is
 * about when to re-ask the server, not about what the answer is. A minute of
 * padding avoids firing a hair early and getting the same day back.
 */
export function msUntilLocalMidnight(now: Date = new Date()): number {
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return Math.max(next.getTime() - now.getTime() + 60_000, 60_000);
}
